import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";
import { BASE_URL_API } from "../../../References/URLs";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import AuthContext from "../../../store/auth-context";
import { FaCheckCircle, FaTimesCircle, FaClock, FaEnvelopeOpen, FaDownload, FaPaperPlane } from "react-icons/fa";
import { FiFilter, FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";

import { motion } from "framer-motion";
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar
} from "recharts";
import styles from "./CampaignPerformance.module.css";
import PerformanceOverTime from "./PerformanceOverTime";
import { IoMdRefresh } from "react-icons/io";
import StepHeader from "./shared/StepHeader";

const COLORS = ["#00C49F", "#FF8042", "#FF0000", "#2816f2", "#FF69B4"]; // Adjusted colors for clarity

const ZERO_DATETIME = "0000-00-00 00:00:00";

// Both delivery-record sources use their own "nothing happened yet" marker
// for a timestamp: the legacy endpoint uses MySQL's zero-datetime sentinel
// (same convention used elsewhere in this app), the new one presumably
// just omits the field / sends null. Treated identically here.
const normalizeTimestamp = (value) => {
    if (!value || value === ZERO_DATETIME) return null;
    return value;
};

// Most-recent of a set of possibly-null timestamp strings, by actual
// parsed time rather than a fixed priority order — used to drive the
// "Performance Over Time" bucketing and the "Last Updated" sort option,
// neither of which has a single dedicated field on either raw shape.
const latestOf = (...timestamps) => {
    let latest = null;
    let latestMs = -Infinity;
    timestamps.forEach((ts) => {
        if (!ts) return;
        const ms = new Date(ts).getTime();
        if (!isNaN(ms) && ms > latestMs) {
            latestMs = ms;
            latest = ts;
        }
    });
    return latest;
};

// Adapter maps the raw getBroadcastCampaignDeliveryRecords shape onto one
// internal shape so the table/charts/CSV export only deal with one set of
// field names. The old legacy getCampaignDeliveryRecords endpoint (and its
// adaptLegacyRecord counterpart) is deprecated — every campaign now goes
// through the broadcast pipeline, so this is the only source left.
const adaptBroadcastRecord = (record) => {
    // "Accepted" is a legacy-only concept — the new pipeline has a single
    // submitted_at event, not a separate accepted+sent pair. This used to
    // map submitted_at onto BOTH acceptedOn and sentOn, which double-
    // counted a single message as 2 in the time-series chart, the daily
    // breakdown table, and the heatmap — all three iterate
    // [acceptedOn, sentOn, deliveredOn, readOn] and increment once per
    // populated field, so having the same event populate two of those
    // fields counted it twice. Confirmed against real production data.
    // Fixed by keeping the "Sent" bucket (reusing its existing label/field
    // name — cheaper than renaming "Sent" to "Submitted" everywhere it
    // appears) as the one canonical bucket fed by submitted_at, and simply
    // not populating acceptedOn at all for this source — the "Accepted"
    // series/tile/checkbox then correctly reads zero for broadcast-sourced
    // campaigns instead of double-counting, with no changes needed in
    // PerformanceOverTime.js/PerformanceOverTimeInHeatmap.js themselves.
    const submittedOn = normalizeTimestamp(record?.submitted_at);
    const deliveredOn = normalizeTimestamp(record?.delivered_at);
    const readOn = normalizeTimestamp(record?.read_at);
    const failedOn = normalizeTimestamp(record?.failed_at);

    const failedResponse = [record?.error_code, record?.error_message]
        .filter(Boolean)
        .join(': ');

    return {
        phoneNumber: record?.recipient_phone || '',
        // Assuming the status vocabulary itself (accepted/sent/delivered/
        // read/failed) is shared between sources and only the field NAME
        // differs, per how this was described — not remapped further.
        status: (record?.status || '').toLowerCase(),
        acceptedOn: null, sentOn: submittedOn, deliveredOn, readOn, failedOn,
        createdOn: submittedOn,
        lastUpdatedOn: latestOf(failedOn, readOn, deliveredOn, submittedOn),
        failedResponse,
        messageId: record?.provider_message_id || '',
    };
};

const CampaignPerformance = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const { campaignIdentifier } = useParams();

    const [isLoadingRecords, setIsLoadingRecords] = useState(true)
    const [deliveryRecords, setDeliveryRecords] = useState([]);

    // No server-side pagination UI added here — the new endpoint supports
    // status/limit/offset, but the existing table already does client-side
    // search/filter/sort over the full loaded set (both the legacy path
    // and, until now, every other list in this app has the same
    // no-pagination shape). Real pagination would mean reworking that
    // client-side filtering to operate correctly against partial pages,
    // which is a bigger change than this task called for — requesting a
    // generous limit instead and keeping today's UX unchanged for both
    // paths. Worth revisiting if a single campaign's audience regularly
    // exceeds this.
    const BROADCAST_RECORDS_LIMIT = 5000;

    const getBroadcastCampaignDeliveryRecords = async () => {
        const response = await fetch(BASE_URL_API + "getBroadcastCampaignDeliveryRecords", {
            method: "POST",
            body: JSON.stringify({
                token: authCtx.token,
                campaignIdentifier,
                limit: BROADCAST_RECORDS_LIMIT,
                offset: 0,
            }),
        });

        if (!response.ok) {
            toast.error(`Couldn't load delivery records. Please try again.`);
            return null;
        }

        const result = await response.json();
        if (result.status === "success") {
            return (result.response || []).map(adaptBroadcastRecord);
        } else if (result.message === "tokenExpired") {
            authCtx.logout();
        } else {
            toast.error(result?.message || `Couldn't load delivery records. Please try again.`);
        }
        return null;
    };

    const loadDeliveryRecords = async () => {
        setIsLoadingRecords(true)
        const records = await getBroadcastCampaignDeliveryRecords();
        if (records) {
            setDeliveryRecords(records);
        }
        setIsLoadingRecords(false)
    };

    useEffect(() => {
        loadDeliveryRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignIdentifier]);

    const statusCounts = deliveryRecords.reduce(
        (acc, record) => {
            acc[record.status] = (acc[record.status] || 0) + 1;
            return acc;
        },
        { accepted: 0, delivered: 0, failed: 0, read: 0, sent: 0 }
    );
    const totalRecords = deliveryRecords.length;
    const pctOfTotal = (count) => totalRecords > 0 ? Math.round((count / totalRecords) * 100) : 0;

    const pieChartData = [
        { name: "Accepted", value: statusCounts.accepted },
        { name: "Delivered", value: statusCounts.delivered },
        { name: "Failed", value: statusCounts.failed },
        { name: "Read", value: statusCounts.read },
        { name: "Sent", value: statusCounts.sent },
    ];

    const getStatusIcon = (status) => {
        switch (status) {
            case "delivered":
                return <FaCheckCircle color="#00C49F" title="Delivered" />;
            case "failed":
                return <FaTimesCircle color="#FF0000" title="Failed" />;
            case "read":
                return <FaEnvelopeOpen color="#2816f2" title="Read" />;  // Open envelope for Read
            case "sent":
                return <FaClock color="#FF69B4" title="Sent" />;  // Clock icon for Sent
            default:
                return <FaClock color="#FF8042" title="Pending" />;  // Default pending
        }
    };

    // **Graphical Insights Based on Timestamps**
    // Previously bucketed by hour-of-day only (getHours(), no date component),
    // which silently merged the same hour across different days into one bar
    // — a campaign whose delivery spans multiple days would misleadingly
    // combine e.g. "3pm Monday" and "3pm Tuesday" into a single "15:00" point.
    // Buckets are now keyed by actual date+hour instead. Records with no
    // real timestamp yet are skipped rather than producing a "NaN:00"
    // bucket — lastUpdatedOn is already null-safe (both adapters normalize
    // each source's own "nothing yet" marker, see normalizeTimestamp).
    const groupedByTime = deliveryRecords.reduce((acc, record) => {
        if (!record.lastUpdatedOn) {
            return acc;
        }
        const timestamp = new Date(record.lastUpdatedOn);
        if (isNaN(timestamp.getTime())) {
            return acc;
        }
        const bucketKey = new Date(
            timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate(), timestamp.getHours()
        ).getTime();

        if (!acc[bucketKey]) {
            acc[bucketKey] = {
                bucketKey,
                time: timestamp.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' }),
                accepted: 0, delivered: 0, failed: 0, read: 0, sent: 0,
            };
        }
        if (acc[bucketKey][record.status] !== undefined) {
            acc[bucketKey][record.status] += 1;
        }
        return acc;
    }, {});

    const timeSeriesData = Object.values(groupedByTime).sort((a, b) => a.bucketKey - b.bucketKey);

    const [statusFilter, setStatusFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("createdOn");
    // Mobile only — collapsed by default to cut down on-screen density,
    // same pattern used by the Message Template step's filter toggle.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const hasActiveDeliveryFilters = statusFilter !== "All" || searchTerm.trim() !== "";

    const filteredAndSortedRecords = useMemo(() => {
        return deliveryRecords
            .filter((record) =>
                statusFilter === "All" ? true : record.status === statusFilter.toLowerCase()
            )
            .filter((record) =>
                Object.values(record).some(
                    (val) => typeof val === "string" && val.toLowerCase().includes(searchTerm.toLowerCase())
                )
            )
            .sort((a, b) =>
                new Date(b[sortBy]) - new Date(a[sortBy])
            );
    }, [deliveryRecords, statusFilter, searchTerm, sortBy]);

    // Real client-side export, replacing the previous "Download report" stub
    // that just showed an alert() saying it needed a premium/admin plan.
    // Same escaped-CSV pattern used by ComAllCampaigns.js/ComAllAudiences.js.
    const exportToCSV = () => {
        if (!filteredAndSortedRecords || filteredAndSortedRecords.length === 0) {
            toast.error(`No records to export.`);
            return;
        }

        const escapeCsvValue = (value) => `"${(value ?? '').toString().replace(/"/g, '""')}"`;
        const headers = ['Phone Number', 'Status', 'Created On', 'Last Updated', 'Read On', 'Failed Response'];
        const rows = filteredAndSortedRecords.map((record) => [
            record.phoneNumber,
            record.status,
            record.createdOn,
            record.lastUpdatedOn,
            record.readOn,
            record.failedResponse,
        ].map(escapeCsvValue).join(','));

        const csvContent = [headers.map(escapeCsvValue).join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `campaign-${campaignIdentifier}-delivery-records.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className={styles.container}>
            <StepHeader
                eyebrow="Step 5 of 5"
                title="Campaign Performance"
                right={
                    <>
                        {isLoadingRecords === false ?
                            <button className={`${styles.loadingIcon}`}
                                title="Reload data.."
                                onClick={() => loadDeliveryRecords()}
                            >
                                <IoMdRefresh size={24} />
                            </button>
                            :
                            <button className={`${styles.loadingIconSpin} `}>
                                <IoMdRefresh size={24} className={`${styles.spinner}`} />
                            </button>
                        }
                        <button
                            className={styles.loadingIcon}
                            title="Download CSV"
                            onClick={exportToCSV}>
                            <FaDownload />
                        </button>
                    </>
                }
            />
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className={styles.chartContainer}
            >
                <div className={styles.summaryTextWrapper}>
                    <div className={styles.tilesWrapper}>
                        {/* Chronological funnel order (accepted -> sent -> delivered ->
                            read), with failed called out separately — each tile's
                            caption is that stage's share of the whole audience, the
                            one meaningful "delta" a single campaign's numbers can show. */}
                        <div className="ccStatTile">
                            <div className="ccStatTop">
                                <span className="ccStatIcon"><FaClock /></span>
                                <span className="ccStatLabel">Accepted</span>
                            </div>
                            <div className="ccStatValue">{statusCounts.accepted}</div>
                            <div className="ccStatDelta">{pctOfTotal(statusCounts.accepted)}% of audience</div>
                        </div>
                        <div className="ccStatTile">
                            <div className="ccStatTop">
                                <span className="ccStatIcon"><FaPaperPlane /></span>
                                <span className="ccStatLabel">Sent</span>
                            </div>
                            <div className="ccStatValue">{statusCounts.sent}</div>
                            <div className="ccStatDelta">{pctOfTotal(statusCounts.sent)}% of audience</div>
                        </div>
                        <div className="ccStatTile">
                            <div className="ccStatTop">
                                <span className="ccStatIcon"><FaCheckCircle /></span>
                                <span className="ccStatLabel">Delivered</span>
                            </div>
                            <div className="ccStatValue">{statusCounts.delivered}</div>
                            <div className="ccStatDelta">{pctOfTotal(statusCounts.delivered)}% of audience</div>
                        </div>
                        <div className="ccStatTile">
                            <div className="ccStatTop">
                                <span className="ccStatIcon"><FaEnvelopeOpen /></span>
                                <span className="ccStatLabel">Read</span>
                            </div>
                            <div className="ccStatValue">{statusCounts.read}</div>
                            <div className="ccStatDelta">{pctOfTotal(statusCounts.read)}% of audience</div>
                        </div>
                        <div className="ccStatTile">
                            <div className="ccStatTop">
                                <span className="ccStatIcon" style={{ background: 'var(--cc-danger-soft)', color: 'var(--cc-danger)' }}><FaTimesCircle /></span>
                                <span className="ccStatLabel">Failed</span>
                            </div>
                            <div className="ccStatValue">{statusCounts.failed}</div>
                            <div className={`ccStatDelta ${statusCounts.failed > 0 ? 'ccNegative' : ''}`}>{pctOfTotal(statusCounts.failed)}% of audience</div>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="60%" height={350}>
                    <PieChart>
                        <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={80} // Adds a donut effect
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                            // Zero-value slices used to render an outside label anyway,
                            // and several of them stacked at the same position collided
                            // into unreadable overlapping text (e.g. "Sent 0.0% 0.0%") —
                            // suppressing the label for empty slices instead of drawing
                            // one that says nothing useful.
                            label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(1)}%` : null}
                            isAnimationActive={true}
                        >
                            {pieChartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                    onMouseEnter={(e) => (e.target.style.transform = "scale(1.001)")}
                                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                                />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} messages`, name]} />
                        <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                </ResponsiveContainer>
            </motion.div>


            {/* Line Chart for Message Status Over Time */}
            <div className={styles.chartContainer}>
                <h3 className={styles.chartHeading}>Performance Over Time</h3>
                <ResponsiveContainer width="90%" height={300}>
                    <LineChart data={timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="accepted" stroke="#8884d8" />
                        <Line type="monotone" dataKey="delivered" stroke="#00C49F" />
                        <Line type="monotone" dataKey="failed" stroke="#FF0000" />

                        <Line type="monotone" dataKey="read" stroke="#2816f2" />
                        <Line type="monotone" dataKey="sent" stroke="#FF69B4" />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <PerformanceOverTime data={deliveryRecords} />

            {/* Table */}
            <div className={styles.filters}>
                <button type="button" className={styles.filtersHeader} onClick={() => setFiltersOpen((prev) => !prev)}>
                    <FiFilter size={14} />
                    <span>Refine delivery list</span>
                    {hasActiveDeliveryFilters && <span className={styles.filterToggleDot} />}
                </button>
                <div className={`${styles.filtersGrid} ${filtersOpen ? styles.filtersGridOpen : ''}`}>
                    <label className={styles.filterField}>
                        <span className={styles.filterLabel}>Search</span>
                        <div className={styles.inputWrapper}>
                            <FiSearch size={14} />
                            <input
                                className={styles.filterInput}
                                type="text"
                                placeholder="Search by phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </label>

                    <label className={styles.filterField}>
                        <span className={styles.filterLabel}>Status</span>
                        <select
                            className={styles.filterSelect}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option>All</option>
                            <option>Sent</option>
                            <option>Delivered</option>
                            <option>Read</option>
                            <option>Accepted</option>
                            <option>Failed</option>
                        </select>
                    </label>

                    <label className={styles.filterField}>
                        <span className={styles.filterLabel}>Sort By</span>
                        <select
                            className={styles.filterSelect}
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="createdOn">Created Date</option>
                            <option value="lastUpdatedOn">Last Updated</option>
                            <option value="readOn">Read Time</option>
                        </select>
                    </label>
                </div>
            </div>

            <div >
                <div>
                    {filteredAndSortedRecords?.length} / {deliveryRecords?.length}
                </div>
            </div>


            <div className={styles.tableContainer}>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
                    {filteredAndSortedRecords.map((record, index) => (
                        <motion.div
                            key={index}
                            className={styles.row}
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.005 }}
                        >
                            <div data-label="Phone">{record.phoneNumber}</div>
                            <div data-label="Status">{getStatusIcon(record.status)} {record.status}</div>
                            <div data-label="Created">{record.createdOn}</div>
                            <div className={styles.smallText} data-label="Response">{record.failedResponse}</div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default CampaignPerformance;
